/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

// Material Dashboard 2 PRO React components
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React examples
import SalesTableCell from "examples/Tables/SalesTable/SalesTableCell";
import { TableCell } from "@mui/material";

function SalesTable({ title, rows, shadow, ignore, action, onActionClick }) {
  const renderTableCells = rows.map((row, key, index) => {
    const tableRows = [];
    const rowKey = `row-${key}`;

    Object.entries(row).map(([cellTitle, cellContent]) =>
      Array.isArray(cellContent) && !ignore?.includes(cellTitle)
        ? tableRows.push(
            <SalesTableCell
              key={cellContent[1]}
              title={cellTitle?.replaceAll("_", " ")}
              content={cellContent[1]}
              image={cellContent[0]}
              button={cellContent}
              noBorder={key === rows.length - 1}
            />
          )
        : !ignore?.includes(cellTitle) &&
          tableRows.push(
            <SalesTableCell
              key={cellContent}
              title={cellTitle?.replaceAll("_", " ")}
              content={cellContent}
              noBorder={key === rows.length - 1}
            />
          )
    );
    action
      ? tableRows.push(
          <TableCell>
            <MDBox display="flex" alignItems="center" width="max-content">
              <MDBox display="flex" flexDirection="column" ml={3}>
                <MDTypography
                  variant="caption"
                  color="text"
                  fontWeight="medium"
                  textTransform="capitalize"
                >
                  {"Action"}:
                </MDTypography>
                <MDBox display="flex" flexDirection="row">
                  {action.action.map(
                    (e) =>
                      e.presenseKey.includes(row[e?.presenseTypeKey]) && (
                        <MDBox
                          mr={1}
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            onActionClick({ action: e.key, id: row[e.idKey] });
                          }}
                        >
                          {e.icon}
                        </MDBox>
                      )
                  )}
                </MDBox>
              </MDBox>
            </MDBox>
          </TableCell>
        )
      : null;

    return <TableRow key={rowKey}>{tableRows}</TableRow>;
  });

  return (
    <TableContainer
      sx={{ height: "100%", boxShadow: !shadow && "none", width: "100%" }}
    >
      <Table sx={{ width: "100%" }}>
        {title ? (
          <TableHead>
            <MDBox component="tr" width="max-content" display="block" mb={1.5}>
              <MDTypography variant="h6" component="td">
                {title}
              </MDTypography>
            </MDBox>
          </TableHead>
        ) : null}
        <TableBody>{useMemo(() => renderTableCells, [rows])}</TableBody>
      </Table>
    </TableContainer>
  );
}
// Setting default values for the props of SalesTable
SalesTable.defaultProps = {
  title: "",
  rows: [{}],
  shadow: true,
};

// Typechecking props for the SalesTable
SalesTable.propTypes = {
  title: PropTypes.string,
  rows: PropTypes.arrayOf(PropTypes.object),
  shadow: PropTypes.bool,
};

export default SalesTable;
